import { useState } from 'react';
import {
  GAME_STATES,
  INITIAL_LIVES,
  DEFAULT_OPERATIONS,
  DIFFICULTY_LEVELS,
  GAME_MODES,
  GAME_MODE_CONFIG,
} from '../config/gameConfig';

/**
 * Custom hook to manage game state
 */
export function useGameState() {
  const [gameState, setGameState] = useState(GAME_STATES.SETUP);
  const [setupStep, setSetupStep] = useState(1);
  const [playerName, setPlayerName] = useState('');
  const [selectedOperations, setSelectedOperations] = useState(DEFAULT_OPERATIONS);
  const [difficulty, setDifficulty] = useState(DIFFICULTY_LEVELS.EASY);
  const [gameMode, setGameMode] = useState(GAME_MODES.NORMAL);

  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [level, setLevel] = useState(1);
  const [lives, setLives] = useState(INITIAL_LIVES);
  const [achievements, setAchievements] = useState([]);
  const [timeRemaining, setTimeRemaining] = useState(null); // For time attack mode

  const nextStep = () => {
    if (setupStep === 1) {
      if (playerName.trim() === '') {
        alert('Indtast venligst dit navn!');
        return false;
      }
      setSetupStep(2);
      return true;
    } else if (setupStep === 2) {
      const hasOperations =
        selectedOperations.plus ||
        selectedOperations.minus ||
        selectedOperations.gange;
      if (!hasOperations) {
        alert('Vælg mindst én type regnestykke!');
        return false;
      }
      setSetupStep(3);
      return true;
    } else if (setupStep === 3) {
      setSetupStep(4);
      return true;
    } else if (setupStep === 4) {
      startGame();
      return true;
    }
    return false;
  };

  const prevStep = () => {
    if (setupStep > 1) {
      setSetupStep(setupStep - 1);
      return true;
    }
    return false;
  };

  const startGame = () => {
    // Initialize lives based on game mode
    const modeConfig = GAME_MODE_CONFIG[gameMode];
    setLives(modeConfig.lives);

    // Initialize timer for time attack mode
    if (modeConfig.hasTimer) {
      setTimeRemaining(modeConfig.timeLimit);
    }

    setGameState(GAME_STATES.PLAYING);
  };

  const endGame = () => {
    setGameState(GAME_STATES.GAME_OVER);
  };

  const resetGame = () => {
    setScore(0);
    setStreak(0);
    setLevel(1);
    setLives(INITIAL_LIVES);
    setAchievements([]);
    setGameState(GAME_STATES.SETUP);
    setSetupStep(1);
  };

  const addScore = (points) => {
    setScore((prev) => prev + points);
  };

  const incrementStreak = () => {
    setStreak((prev) => prev + 1);
  };

  const resetStreak = () => {
    setStreak(0);
  };

  const loseLife = () => {
    setLives((prev) => Math.max(0, prev - 1));
    return lives - 1; // Return new lives count
  };

  const levelUp = () => {
    setLevel((prev) => prev + 1);
  };

  const addAchievement = (achievement) => {
    setAchievements((prev) => [...prev, achievement]);
  };

  const removeFirstAchievement = () => {
    setAchievements((prev) => prev.slice(1));
  };

  const toggleOperation = (operation) => {
    setSelectedOperations((prev) => ({
      ...prev,
      [operation]: !prev[operation],
    }));
  };

  return {
    // State
    gameState,
    setupStep,
    playerName,
    selectedOperations,
    difficulty,
    gameMode,
    score,
    streak,
    level,
    lives,
    achievements,
    timeRemaining,

    // Setup actions
    setPlayerName,
    setDifficulty,
    setGameMode,
    toggleOperation,
    nextStep,
    prevStep,

    // Game actions
    startGame,
    endGame,
    resetGame,
    addScore,
    incrementStreak,
    resetStreak,
    loseLife,
    levelUp,
    addAchievement,
    removeFirstAchievement,
    setTimeRemaining,
  };
}
